import {
  ComplianceDocument,
  FolderStructureResponse,
  MarketingAssetFileDetailSchema,
} from '@/lib/types';
import FileListTable from './FileListTable';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState, ReactNode } from 'react';
import Button from '../Button/Button';
import { FormattedMessage } from 'react-intl';
import DownloadIcon from '@/assets/download.svg?react';
import TrashIcon from '@/assets/trash.svg?react';
import ModeSwitch from './ModeSwitch';
import FileGrid from './FileGrid';
import FilterIcon from '@/assets/filter.svg?react';
import Popper from '@/components/Popper/Popper';
import FileExplorerComplianceFilters from './FileExplorerComplianceFilters';
import FileExplorerMarketingFilters from './FileExplorerMarketingFilters';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { flattenDeep } from 'lodash';
import { ComplianceFileTypeConfig } from '@/constants/FileTypesCompliance';

const FileExplorer = ({
  loading,
  files,
  filesFlatList,
  selected,
  onSelect,
  onDownloadClick,
  onRemoveClick,
  showDownload = true,
  selectedCount,
  showSelectedLabel = true,
  totalCount,
  showRemove = false,
  type = 'compliance',
  showFilters,
  filters,
  onFiltersChange,
  fileTypeComplianceConfig,
  showHeader = true,
  path,
  onPathChange,
  onFileDelete,
  hiddenRoot,
  downloadLoading,
  removeLoading,
  loadingNextPage,
  actions,
  trailingActions,
  sortControl,
  hasActiveSearchOrFilter,
  currentView,
  onViewChange,
  exactMatchQuery,
}: {
  folderStructure?: FolderStructureResponse;
  loading?: boolean;
  files?: ComplianceDocument[] | MarketingAssetFileDetailSchema[];
  filesFlatList?:
    ComplianceDocument[] | MarketingAssetFileDetailSchema[];
  selected?: number[];
  onSelect?: (selected: number[]) => void;
  onDownloadClick?: () => void;
  onRemoveClick?: () => void;
  showDownload?: boolean;
  // Overrides the count shown in the label and used to enable the action
  // buttons - lets a host scope them to the rows on screen when `selected`
  // is a wider, app-level selection.
  selectedCount?: number;
  showSelectedLabel?: boolean;
  // Total files matching the current filter, shown next to the
  // selected count; omit to hide it.
  totalCount?: number;
  showRemove?: boolean;
  type?: 'compliance' | 'marketing' | 'game';
  showFilters?: boolean;
  filters?: Partial<FilterSchema>;
  onFiltersChange?: (filters: Partial<FilterSchema>) => void;
  fileTypeComplianceConfig?: ComplianceFileTypeConfig;
  showHeader?: boolean;
  path: string[];
  onPathChange: (path: string[]) => void;
  onFileDelete?: () => void;
  hiddenRoot?: string;
  downloadLoading?: boolean;
  removeLoading?: boolean;
  loadingNextPage?: boolean;
  actions?: ReactNode;
  // Rendered after Remove, for actions that sit next to it.
  trailingActions?: ReactNode;
  // Rendered last before the view toggle.
  sortControl?: ReactNode;
  hasActiveSearchOrFilter?: boolean;
  onAllFilesClick?: () => void;
  currentView?: 'list' | 'grid';
  onViewChange?: (view: 'list' | 'grid') => void;
  exactMatchQuery?: string;
}) => {
  const effectiveSelectedCount =
    selectedCount ?? selected?.length ?? 0;
  const filterRef = useRef<HTMLButtonElement>(null);
  const [mode, setMode] = useState<'list' | 'grid'>(
    currentView ?? 'grid',
  );

  useEffect(() => {
    if (currentView) {
      setMode(currentView);
    }
  }, [currentView]);

  const handleModeChange = (newMode: 'list' | 'grid') => {
    if (!currentView) {
      setMode(newMode);
    }
    onViewChange?.(newMode);
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showNotification =
    flattenDeep<
      | string
      | number
      | boolean
      | string[]
      | number[]
      | [string, string]
    >(Object.values(filters || {})).length > 0;

  useEffect(() => {
    if (path.length === 0 && hiddenRoot) {
      onPathChange([hiddenRoot]);
    }
  }, [path, files, hiddenRoot]);

  const showSelected =
    showSelectedLabel && effectiveSelectedCount > 0;
  // One label under the actions: the total for the current filter and,
  // once something is picked, how many of them are selected.
  const countLabel =
    totalCount !== undefined ? (
      showSelected ? (
        <FormattedMessage
          id="filesSelectedOfTotal"
          defaultMessage="{selected} of {count, plural, one {{total} file} other {{total} files}} selected"
          values={{
            count: totalCount,
            selected: <b>{effectiveSelectedCount}</b>,
            total: <b>{totalCount}</b>,
          }}
        />
      ) : (
        <FormattedMessage
          id="fileCount"
          defaultMessage="{count, plural, one {{total} file} other {{total} files}}"
          values={{
            count: totalCount,
            total: <b>{totalCount}</b>,
          }}
        />
      )
    ) : showSelected ? (
      <FormattedMessage
        id="itemsSelected"
        defaultMessage="{count} items selected"
        values={{ count: <b>{effectiveSelectedCount}</b> }}
      />
    ) : null;

  return (
    <Root aria-label="File explorer">
      {showHeader && (
        <HeaderBlock>
          <Header>
            <Actions>
              {actions ? (
                <ActionButtons>{actions}</ActionButtons>
              ) : null}
              {showDownload ? (
                <Button
                  variant="contained"
                  onClick={onDownloadClick}
                  disabled={!effectiveSelectedCount}
                  aria-label="Download selected"
                  loading={downloadLoading}
                >
                  <DownloadIcon />
                  <FormattedMessage
                    id="downloadWithCount"
                    defaultMessage="Download{suffix}"
                    values={{
                      suffix: effectiveSelectedCount
                        ? ` (${effectiveSelectedCount})`
                        : '',
                    }}
                  />
                </Button>
              ) : null}
              {showRemove ? (
                <Button
                  variant="outlined"
                  onClick={onRemoveClick}
                  disabled={!effectiveSelectedCount}
                  aria-label="Remove selected"
                  loading={removeLoading}
                >
                  <TrashIcon />
                  <FormattedMessage
                    id="remove"
                    defaultMessage="Remove"
                  />
                </Button>
              ) : null}
              {trailingActions}
              {showFilters ? (
                <>
                  <Button
                    variant="outlined"
                    icon
                    ref={filterRef}
                    onClick={() => setFiltersOpen((v) => !v)}
                    aria-label="Filter Button"
                  >
                    <FilterIcon />
                    {showNotification && <FilterNotification />}
                  </Button>
                  <Popper
                    aria-label="Filters Popper"
                    open={filtersOpen}
                    anchorEl={filterRef.current}
                    onClose={() => setFiltersOpen(false)}
                    offsetY={10}
                  >
                    {type === 'compliance' &&
                    filters &&
                    onFiltersChange ? (
                      <FileExplorerComplianceFilters
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        fileTypeConfig={fileTypeComplianceConfig}
                      />
                    ) : null}
                    {type === 'marketing' &&
                    filters &&
                    onFiltersChange ? (
                      <FileExplorerMarketingFilters
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                      />
                    ) : null}
                  </Popper>
                </>
              ) : null}
              {sortControl}
              {onViewChange ? (
                <ModeSwitch mode={mode} onChange={handleModeChange} />
              ) : null}
            </Actions>
          </Header>
          {countLabel ? (
            <CountRow>
              <CountLabel aria-label="File count">
                {countLabel}
              </CountLabel>
            </CountRow>
          ) : null}
        </HeaderBlock>
      )}
      {mode === 'list' ? (
        <FileListTable
          files={[...(filesFlatList ?? files ?? [])]}
          loading={loading}
          loadingNextPage={loadingNextPage}
          type={type === 'compliance' ? 'compliance' : 'marketing'}
          selected={selected}
          onSelect={onSelect}
          onFileDelete={onFileDelete}
          downloadLoading={downloadLoading}
          hasActiveSearchOrFilter={hasActiveSearchOrFilter}
          exactMatchQuery={exactMatchQuery}
        />
      ) : (
        <FileGrid
          files={[...(filesFlatList ?? files ?? [])]}
          selected={selected}
          onSelect={onSelect}
          type={type}
          loading={loading}
          exactMatchQuery={exactMatchQuery}
        />
      )}
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const HeaderBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const CountRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
});

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginLeft: 'auto',
  alignItems: 'center',
}));

const ActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

const CountLabel = styled('div')(({ theme }) => ({
  whiteSpace: 'nowrap',
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
  color: theme.palette.grey500,

  b: {
    fontWeight: 500,
  },
}));

const FilterNotification = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  position: 'absolute',
  top: -4,
  right: -4,
}));

// const LoaderContainer = styled('div')(() => ({
//   width: '100%',
//   padding: '60px',
//   display: 'flex',
//   justifyContent: 'center',
// }));

export default FileExplorer;
